import { nativeNotifications } from '@/features/location/native-bridge';

/**
 * One tray notice for the chat, replaced rather than stacked: the count is in
 * the text, so a second message rewrites the notice instead of adding to a
 * pile the reader then has to sweep.
 */
export const CHAT_NOTICE_ID = 1;

/**
 * Our own channel, at Android's "high" importance.
 *
 * Not the plugin's built-in "default" one, which sits at importance 3: that
 * makes a sound but never shows the banner over whatever is on screen, and a
 * channel's importance is fixed the moment Android registers it — it can only
 * be raised by using a different channel. A message from a colleague is worth
 * the banner every other messenger gives it, so this is 4.
 */
export const CHAT_NOTICE_CHANNEL = 'chat-messages';

/**
 * Registers the channel, once per launch and before any notice is scheduled.
 *
 * Creating a channel that exists is a no-op, so this needs no bookkeeping.
 * Does nothing in a browser, and nothing in a shell built before the plugin
 * (up to v1.3) — where `createChannel` is also missing on newer plugin
 * versions that dropped it, hence the optional call.
 */
export async function ensureNoticeChannel(): Promise<void> {
  const plugin = nativeNotifications();
  if (!plugin?.createChannel) return;
  try {
    await plugin.createChannel({
      id: CHAT_NOTICE_CHANNEL,
      name: 'Messages',
      description: 'Somebody wrote to you in the chat',
      // 4 = IMPORTANCE_HIGH: sound, and a banner over the current screen.
      importance: 4,
      // 0 = VISIBILITY_PRIVATE: on a locked screen the notice shows, its text
      // does not. Chat is between two people; the lock screen is not a party.
      visibility: 0,
      vibration: true,
    });
  } catch {
    // A shell that refuses the channel still gets the plugin's default one.
  }
}

/** Takes the chat notice out of the tray — everything has been read. */
export async function clearNotice(): Promise<void> {
  const plugin = nativeNotifications();
  if (!plugin) return;
  try {
    await plugin.cancel({ notifications: [{ id: CHAT_NOTICE_ID }] });
  } catch {
    // Nothing to take back, or an older shell without `cancel`.
  }
}
